import clsx from 'clsx';
import moment from 'moment-timezone';
import { Dispatch, SetStateAction, useState } from 'react';

import { Button } from '@@/buttons';
import { Modal } from '@@/modals';
import { NavTabs } from '@@/NavTabs';

import { Certificate, Name } from '../types';

import styles from './CertificateModal.module.css';

export function CertificateModalButton({
  certificate,
}: {
  certificate?: Certificate;
}) {
  const [isCertificateModalVisible, setIsCertificateModalVisible] =
    useState(false);
  const [certificateModalTab, setCertificateModalTab] = useState('summary');

  return (
    <>
      {certificate && (
        <Button
          onClick={() => setIsCertificateModalVisible(true)}
          data-cy="show-cert"
          title="Show Cert"
          color="secondary"
        >
          Show Certificate
        </Button>
      )}
      {isCertificateModalVisible && certificate && (
        <CertificateModal
          cert={certificate}
          closeModal={() => setIsCertificateModalVisible(false)}
          tab={certificateModalTab}
          setTab={setCertificateModalTab}
        />
      )}
    </>
  );
}

function CertificateModal({
  cert,
  closeModal,
  tab,
  setTab,
}: {
  cert: Certificate;
  closeModal: () => void;
  tab: string;
  setTab: Dispatch<SetStateAction<string>>;
}) {
  return (
    <Modal onDismiss={closeModal} aria-label="mTLS Certificate" size="lg">
      <Modal.Header title="Server MTLS Certificate" />

      <Modal.Body>
        <NavTabs<string>
          onSelect={setTab}
          selectedId={tab}
          type="tabs"
          justified
          options={[
            {
              id: 'summary',
              label: 'Summary',
              children: (
                <div
                  className={clsx(
                    styles.certModal,
                    'max-h-[80vh] overflow-auto'
                  )}
                >
                  <CertificateSummary certificate={cert} />
                </div>
              ),
            },
            {
              id: 'details',
              label: 'Details',
              children: (
                <div
                  className={clsx(
                    styles.certModal,
                    'max-h-[80vh] overflow-auto'
                  )}
                >
                  <CertificateDetails certificate={cert} />
                </div>
              ),
            },
          ]}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          className="w-full"
          onClick={closeModal}
          data-cy="portainerMTLSCertificateModal-CloseButton"
        >
          Ok
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function CertificateSummary({ certificate }: { certificate: Certificate }) {
  return (
    <>
      <SectionTitle title="Subject Name" />
      {certificate.Subject && <NameComponent name={certificate.Subject} />}

      <br />
      <SectionTitle title="Issuer Name" />
      {certificate.Issuer && <NameComponent name={certificate.Issuer} />}

      <br />
      <Section
        title="Validity"
        sectionItems={[
          {
            name: 'Not Before',
            value: formatDate(certificate.ValidNotBefore),
          },
          {
            name: 'Not After',
            value: formatDate(certificate.ValidNotAfter),
          },
        ]}
      />

      <br />
      <Section
        title="Fingerprints"
        sectionItems={[
          {
            name: 'SHA-256',
            value: certificate.SHA256Fingerprint,
          },
          {
            name: 'SHA-1',
            value: certificate.SHA1Fingerprint,
          },
        ]}
      />
    </>
  );
}

export function CertificateDetails({
  certificate,
}: {
  certificate: Certificate;
}) {
  return (
    <>
      <SectionTitle title="Subject Name" />
      {certificate.Subject && <NameComponent name={certificate.Subject} />}

      <br />
      <SectionTitle title="Issuer Name" />
      {certificate.Issuer && <NameComponent name={certificate.Issuer} />}

      <br />
      <Section
        title="Validity"
        sectionItems={[
          {
            name: 'Not Before',
            value: formatDate(certificate.ValidNotBefore),
          },
          {
            name: 'Not After',
            value: formatDate(certificate.ValidNotAfter),
          },
        ]}
      />

      <br />
      <Section
        title="Subject Alt Names"
        sectionItems={[
          ...(certificate.SubjectAltDNSNames?.map((name) => ({
            name: 'DNS Name',
            value: name,
          })) ?? []),
          ...(certificate.SubjectAltEmailAddresses?.map((email) => ({
            name: 'Email Address',
            value: email,
          })) ?? []),
          ...(certificate.SubjectAltIpAddresses?.map((ip) => ({
            name: 'IP Address',
            value: ip,
          })) ?? []),
          ...(certificate.SubjectAltURIs?.map((uri) => ({
            name: 'URI',
            value: uri,
          })) ?? []),
        ]}
      />

      <br />
      <Section
        title="Public Key Info"
        sectionItems={[
          {
            name: 'Algorithm',
            value: certificate.PublicKey.Algorithm,
          },
          {
            name: 'Key Size',
            value: certificate.PublicKey.Size,
          },
          ...(certificate.PublicKey.Exponent &&
          certificate.PublicKey.Exponent !== ''
            ? [
                {
                  name: 'Exponent',
                  value: certificate.PublicKey.Exponent,
                },
              ]
            : []),
          ...(certificate.PublicKey.Modulus &&
          certificate.PublicKey.Modulus !== ''
            ? [
                {
                  name: 'Modulus',
                  value: certificate.PublicKey.Modulus,
                },
              ]
            : []),
          ...(certificate.PublicKey.Value && certificate.PublicKey.Value !== ''
            ? [
                {
                  name: 'Value',
                  value: certificate.PublicKey.Value,
                },
              ]
            : []),
        ]}
      />

      <br />
      <Section
        title="Miscellaneous"
        sectionItems={[
          {
            name: 'Serial Number',
            value: certificate.SerialNumber,
          },
          {
            name: 'Signature Algorithm',
            value: certificate.SignatureAlgorithm,
          },
          { name: 'Version', value: certificate.Version },
        ]}
      />

      <br />
      <Section
        title="Fingerprints"
        sectionItems={[
          {
            name: 'SHA-256',
            value: certificate.SHA256Fingerprint,
          },
          {
            name: 'SHA-1',
            value: certificate.SHA1Fingerprint,
          },
        ]}
      />

      <br />
      <Section
        title="Basic Contraints"
        sectionItems={[
          {
            name: 'Certificate Authority',
            value: certificate.IsCertificateAuthority ? 'Yes' : 'No',
          },
        ]}
      />

      <br />
      <Section
        title="Key Usages"
        sectionItems={[
          {
            name: 'Purposes',
            value: certificate.KeyUsages && certificate.KeyUsages.join(','),
          },
        ]}
      />

      <br />
      <Section
        title="Extended Key Usages"
        sectionItems={[
          {
            name: 'Purposes',
            value:
              certificate.ExtendedKeyUsages &&
              certificate.ExtendedKeyUsages.join(','),
          },
        ]}
      />

      <br />
      <Section
        title="Subject Key ID"
        sectionItems={[
          {
            name: 'Key ID',
            value: certificate.SubjectKeyId,
          },
        ]}
      />

      <br />
      <Section
        title="Authority Key ID"
        sectionItems={[
          {
            name: 'Key ID',
            value: certificate.AuthorityKeyId,
          },
        ]}
      />

      {certificate.CRLDistributionPoints && (
        <>
          <br />
          <Section
            title="CRL Endpoints"
            sectionItems={certificate.CRLDistributionPoints.map((dp) => ({
              name: 'Distribution Point',
              value: dp,
            }))}
          />
        </>
      )}

      {(certificate.OCSPServers || certificate.IssuingCertificateURLs) && (
        <>
          <br />
          <Section
            title="Authority Info (AIA)"
            sectionItems={[
              ...(certificate.OCSPServers?.flatMap((server) => [
                { name: 'Location', value: server },
                {
                  name: 'Method',
                  value: 'Online Certificate Status Protocol (OCSP)',
                },
              ]) ?? []),
              ...(certificate.IssuingCertificateURLs?.flatMap((url) => [
                { name: 'Location', value: url },
                { name: 'Method', value: 'CA Issuers' },
              ]) ?? []),
            ]}
          />
        </>
      )}

      {certificate.Policies && certificate.Policies.length > 0 && (
        <>
          <br />
          <Section
            title="Certificate Policies"
            sectionItems={certificate.Policies.map((policy) => ({
              name: 'Policy',
              value: policy,
            }))}
          />
        </>
      )}
    </>
  );
}

const dateTimeFormat = 'ddd, D MMM YYYY HH:mm:ss zz';

function formatDate(d?: Date) {
  if (d) {
    return (
      moment(d).format(dateTimeFormat) + moment.tz(moment.tz.guess()).zoneAbbr()
    );
  }
  return '';
}

interface Item {
  name: string;
  value: string | number | undefined;
}

function SectionTitle({ title }: { title: string }) {
  return <h4>{title}</h4>;
}

function SectionItem({
  name,
  value,
}: {
  name: string;
  value: string | number | undefined;
}) {
  return (
    <tr>
      <td className={styles.sectionItemCell}>{name}</td>
      <td className={styles.sectionItemCell}>{value}</td>
    </tr>
  );
}

function Section({
  title,
  sectionItems,
}: {
  title: string;
  sectionItems: Array<Item> | undefined;
}) {
  return (
    <>
      <SectionTitle title={title} />
      {sectionItems && (
        <table>
          <tbody>
            {sectionItems.map(({ name, value }) => (
              <SectionItem key={`${title}_${name}`} name={name} value={value} />
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function NameComponent({ name }: { name: Name }) {
  return (
    <table>
      <tbody>
        {name.Country && name.Country.length > 0 && (
          <SectionItem name="Country" value={name.Country.join(',')} />
        )}
        {name.Province && name.Province.length > 0 && (
          <SectionItem name="State/Province" value={name.Province.join(',')} />
        )}
        {name.Locality && name.Locality.length > 0 && (
          <SectionItem name="Locality" value={name.Locality.join(',')} />
        )}
        {name.StreetAddress && name.StreetAddress.length > 0 && (
          <SectionItem
            name="Street Address"
            value={name.StreetAddress.join(',')}
          />
        )}
        {name.PostalCode && name.PostalCode.length > 0 && (
          <SectionItem name="Postal Code" value={name.PostalCode.join(',')} />
        )}
        {name.Organization && name.Organization.length > 0 && (
          <SectionItem
            name="Organization"
            value={name.Organization.join(',')}
          />
        )}
        {name.OrganizationUnit && name.OrganizationUnit.length > 0 && (
          <SectionItem
            name="Organization Unit"
            value={name.OrganizationUnit.join(',')}
          />
        )}
        <SectionItem name="Common Name" value={name.CommonName} />
      </tbody>
    </table>
  );
}
